import React, { useState, useEffect, useRef } from "react";

const PHONE = "(817) 438-2050";

const navy = "#161C2C";
const gold = "#C7B167";
const cream = "#F7F4EC";
const wrap = { maxWidth: 1100, margin: "0 auto", padding: "0 24px" };

function Reveal({ children, delay = 0, style = {} }) {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(28px)", transition: "all .7s cubic-bezier(.22,1,.36,1)", ...style }}>
      {children}
    </div>
  );
}

export default function ServicesPage() {
  return (
    <>
      
      {/* HERO */}
      <section style={{ paddingTop: 115, paddingBottom: 60, background: navy, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 50% at 50% 30%, rgba(199,177,103,.1) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ ...wrap, position: "relative", zIndex: 2, textAlign: "center" }}>
          <Reveal>
            <p style={{ fontFamily: "var(--body)", fontSize: 13, fontWeight: 600, color: gold, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 16 }}>What We Do</p>
            <h1 style={{ fontFamily: "var(--head)", fontWeight: 900, fontSize: "clamp(32px,4vw,52px)", lineHeight: 1.1, color: "#fff", marginBottom: 20 }}>Services We Offer</h1>
            <p style={{ fontFamily: "var(--body)", fontSize: 17, lineHeight: 1.75, color: "rgba(255,255,255,.6)", maxWidth: 560, margin: "0 auto" }}>AI-powered automation and professional web design to help your business grow and never miss a lead.</p>
          </Reveal>
        </div>
      </section>

      {/* 1. AI VOICE RECEPTIONIST */}
      <section style={{ padding: "80px 0", background: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 900 }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, alignItems: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", order: 0 }}>
                <VoiceDemo />
              </div>
              <div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 50, background: "rgba(199,177,103,.1)", border: "1px solid rgba(199,177,103,.25)", marginBottom: 20 }}>
                  <span style={{ fontSize: 14 }}>🎙️</span>
                  <span style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 600, color: gold }}>Now Live</span>
                </div>
                <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,38px)", color: navy, marginBottom: 16 }}>AI Voice Receptionist</h2>
                <p style={{ fontFamily: "var(--body)", fontSize: 15, lineHeight: 1.75, color: "#555", marginBottom: 24 }}>An AI-powered receptionist that answers every call, schedules appointments, handles customer questions, and transfers urgent calls to you. Works 24/7 without breaks, sick days, or training.</p>
                <div style={{ marginBottom: 24 }}>
                  {["Answers calls live with a natural AI voice", "Schedules, reschedules & cancels appointments", "Handles FAQs about your business", "Transfers urgent calls directly to you", "Full call summaries & transcripts", "No hiring, no training, no overhead"].map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>✓</span>
                      <span style={{ fontFamily: "var(--body)", fontSize: 14, color: "#444" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <a href="tel:8174382050" style={{ display: "inline-block", padding: "14px 28px", background: navy, color: "#fff", borderRadius: 8, textDecoration: "none", fontFamily: "var(--body)", fontWeight: 700, fontSize: 15 }}>Get Started — {PHONE}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div style={{ maxWidth: 900, margin: "0 auto", height: 1, background: "linear-gradient(90deg, transparent, " + gold + ", transparent)" }} />

      {/* 2. AI TEXT-BACK */}
      <section style={{ padding: "80px 0", background: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 900 }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, alignItems: "center" }}>
              <div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 50, background: "rgba(199,177,103,.1)", border: "1px solid rgba(199,177,103,.25)", marginBottom: 20 }}>
                  <span style={{ fontSize: 14 }}>💬</span>
                  <span style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 600, color: gold }}>Our Flagship Service</span>
                </div>
                <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,38px)", color: navy, marginBottom: 16 }}>AI Missed Call Text-Back</h2>
                <p style={{ fontFamily: "var(--body)", fontSize: 15, lineHeight: 1.75, color: "#555", marginBottom: 24 }}>Never lose a job to a missed call again. Our AI responds to your customers within 30 seconds, handles the conversation professionally, and books the appointment — 24/7, even while you sleep.</p>
                <div style={{ marginBottom: 24 }}>
                  {["Missed call text-back in 30 seconds", "AI-powered natural conversation", "Automatic appointment booking", "Works nights, weekends, and holidays", "Custom setup tailored to your business", "No tech skills needed — we handle everything"].map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>✓</span>
                      <span style={{ fontFamily: "var(--body)", fontSize: 14, color: "#444" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <a href="tel:8174382050" style={{ display: "inline-block", padding: "14px 28px", background: navy, color: "#fff", borderRadius: 8, textDecoration: "none", fontFamily: "var(--body)", fontWeight: 700, fontSize: 15 }}>Get Started — Call {PHONE}</a>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <TextBackDemo />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div style={{ maxWidth: 900, margin: "0 auto", height: 1, background: "linear-gradient(90deg, transparent, " + gold + ", transparent)" }} />

      {/* 3. WEBSITE DESIGN */}
      <section style={{ padding: "80px 0", background: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 900 }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, alignItems: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", order: 0 }}>
                <WebsiteDemo />
              </div>
              <div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 50, background: "rgba(22,28,44,.05)", marginBottom: 20 }}>
                  <span style={{ fontSize: 12 }}>🏆</span>
                  <span style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 600, color: navy }}>6+ Years Web Design Experience</span>
                </div>
                <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,38px)", color: navy, marginBottom: 16 }}>Website Design</h2>
                <p style={{ fontFamily: "var(--body)", fontSize: 15, lineHeight: 1.75, color: "#555", marginBottom: 24 }}>Need a professional website that actually converts? We design and build clean, modern websites for home service businesses — so your customers can find you and trust you online.</p>
                <div style={{ marginBottom: 24 }}>
                  {["Custom design for your brand", "Mobile-friendly & lightning fast", "SEO-optimized to rank on Google", "Contact forms & booking links", "Ongoing support available"].map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>✓</span>
                      <span style={{ fontFamily: "var(--body)", fontSize: 14, color: "#444" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <a href="tel:8174382050" style={{ display: "inline-block", padding: "14px 28px", background: navy, color: "#fff", borderRadius: 8, textDecoration: "none", fontFamily: "var(--body)", fontWeight: 700, fontSize: 15 }}>Get a Quote — {PHONE}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div style={{ maxWidth: 900, margin: "0 auto", height: 1, background: "linear-gradient(90deg, transparent, " + gold + ", transparent)" }} />

      {/* 4. WEBSITE MAINTENANCE */}
      <section style={{ padding: "80px 0", background: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 900 }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, alignItems: "center" }}>
              <div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 50, background: "rgba(199,177,103,.1)", border: "1px solid rgba(199,177,103,.25)", marginBottom: 20 }}>
                  <span style={{ fontSize: 14 }}>🔧</span>
                  <span style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 600, color: gold }}>Keep Your Site Running Smooth</span>
                </div>
                <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,38px)", color: navy, marginBottom: 16 }}>Website Maintenance</h2>
                <p style={{ fontFamily: "var(--body)", fontSize: 15, lineHeight: 1.75, color: "#555", marginBottom: 24 }}>Your website needs regular care to stay fast, secure, and up to date. We handle all the technical stuff so you can focus on running your business.</p>
                <div style={{ marginBottom: 24 }}>
                  {["Monthly updates and security patches", "Performance monitoring and speed optimization", "Content updates when you need them", "Uptime monitoring — we fix issues before you notice", "Priority support via phone and email", "Monthly report on site health and traffic"].map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>✓</span>
                      <span style={{ fontFamily: "var(--body)", fontSize: 14, color: "#444" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <a href="tel:8174382050" style={{ display: "inline-block", padding: "14px 28px", background: navy, color: "#fff", borderRadius: 8, textDecoration: "none", fontFamily: "var(--body)", fontWeight: 700, fontSize: 15 }}>Get Started — {PHONE}</a>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <MaintenanceDemo />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div style={{ maxWidth: 900, margin: "0 auto", height: 1, background: "linear-gradient(90deg, transparent, " + gold + ", transparent)" }} />

      {/* 5. AI WEBSITE CHATBOT */}
      <section style={{ padding: "80px 0", background: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 900 }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, alignItems: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", order: 0 }}>
                <ChatbotDemo />
              </div>
              <div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 50, background: "rgba(199,177,103,.1)", border: "1px solid rgba(199,177,103,.25)", marginBottom: 20 }}>
                  <span style={{ fontSize: 14 }}>🤖</span>
                  <span style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 600, color: gold }}>Capture Every Website Visitor</span>
                </div>
                <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,38px)", color: navy, marginBottom: 16 }}>AI Website Chatbot</h2>
                <p style={{ fontFamily: "var(--body)", fontSize: 15, lineHeight: 1.75, color: "#555", marginBottom: 24 }}>Turn website visitors into booked appointments. Our AI chatbot lives on your website 24/7 — greeting visitors, answering questions, and booking jobs on the spot.</p>
                <div style={{ marginBottom: 24 }}>
                  {["AI-powered conversations — not a scripted bot", "Answers questions about your services", "Books appointments directly on your calendar", "Captures leads even at 2am", "Custom trained on your specific business", "Works alongside AI Text-Back for full coverage"].map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>✓</span>
                      <span style={{ fontFamily: "var(--body)", fontSize: 14, color: "#444" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <a href="tel:8174382050" style={{ display: "inline-block", padding: "14px 28px", background: navy, color: "#fff", borderRadius: 8, textDecoration: "none", fontFamily: "var(--body)", fontWeight: 700, fontSize: 15 }}>Get Started — {PHONE}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div style={{ maxWidth: 900, margin: "0 auto", height: 1, background: "linear-gradient(90deg, transparent, " + gold + ", transparent)" }} />

      {/* 6. GOOGLE REVIEW AUTOMATION */}
      <section style={{ padding: "80px 0", background: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 900 }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, alignItems: "center" }}>
              <div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 50, background: "rgba(199,177,103,.1)", border: "1px solid rgba(199,177,103,.25)", marginBottom: 20 }}>
                  <span style={{ fontSize: 14 }}>⭐</span>
                  <span style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 600, color: gold }}>More Reviews = More Calls</span>
                </div>
                <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,38px)", color: navy, marginBottom: 16 }}>Google Review Automation</h2>
                <p style={{ fontFamily: "var(--body)", fontSize: 15, lineHeight: 1.75, color: "#555", marginBottom: 24 }}>Automatically text your customers after every job asking for a Google review. Most businesses see a surge in reviews within the first 30 days — which means more visibility and more inbound calls from Google.</p>
                <div style={{ marginBottom: 24 }}>
                  {["Auto-texts customers after every completed job", "Direct link to your Google review page", "Doubles most client reviews within 90 days", "More reviews = higher on Google = more calls", "Works silently in the background", "Set it once and let it run forever"].map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>✓</span>
                      <span style={{ fontFamily: "var(--body)", fontSize: 14, color: "#444" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <a href="tel:8174382050" style={{ display: "inline-block", padding: "14px 28px", background: navy, color: "#fff", borderRadius: 8, textDecoration: "none", fontFamily: "var(--body)", fontWeight: 700, fontSize: 15 }}>Get Started — {PHONE}</a>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ReviewDemo />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div style={{ maxWidth: 900, margin: "0 auto", height: 1, background: "linear-gradient(90deg, transparent, " + gold + ", transparent)" }} />

      {/* 7. FINANCIAL INTELLIGENCE */}
      <section style={{ padding: "80px 0", background: "#fff" }}>
        <div style={{ ...wrap, maxWidth: 900 }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50, alignItems: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", order: 0 }}>
                <FinanceDemo />
              </div>
              <div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 50, background: "rgba(22,28,44,.05)", marginBottom: 20 }}>
                  <span style={{ fontSize: 12 }}>🏆</span>
                  <span style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 600, color: navy }}>10+ Years Financial Experience</span>
                </div>
                <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,38px)", color: navy, marginBottom: 16 }}>Financial Intelligence</h2>
                <p style={{ fontFamily: "var(--body)", fontSize: 15, lineHeight: 1.75, color: "#555", marginBottom: 24 }}>Monthly financial reporting built for small business owners. We deliver a clean PDF report by the 10th of every month — plain-English insights that tell you exactly what you made, what you can pay yourself, and where your money went.</p>
                <div style={{ marginBottom: 24 }}>
                  {["Monthly P&L — see exactly what you made and spent", "Know what you can safely pay yourself this month", "Tax set-aside estimate so you are never surprised", "Spot unusual expenses before they become problems", "Year-end projection based on current pace", "Delivered as a clean PDF by the 10th every month"].map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>✓</span>
                      <span style={{ fontFamily: "var(--body)", fontSize: 14, color: "#444" }}>{f}</span>
                    </div>
                  ))}
                </div>
                <a href="tel:8174382050" style={{ display: "inline-block", padding: "14px 28px", background: navy, color: "#fff", borderRadius: 8, textDecoration: "none", fontFamily: "var(--body)", fontWeight: 700, fontSize: 15 }}>Get Started — {PHONE}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "60px 0", background: navy }}>
        <div style={{ ...wrap, textAlign: "center" }}>
          <Reveal>
            <h2 style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: "clamp(26px,3vw,36px)", color: "#fff", marginBottom: 12 }}>Ready to Get Started?</h2>
            <p style={{ fontFamily: "var(--body)", color: "rgba(255,255,255,.55)", fontSize: 16, marginBottom: 28 }}>Let us talk about what your business needs.</p>
            <a href="tel:8174382050" style={{ padding: "14px 32px", background: gold, color: navy, borderRadius: 8, textDecoration: "none", fontSize: 15, fontWeight: 700, fontFamily: "var(--body)" }}>📞 {PHONE}</a>
          </Reveal>
        </div>
      </section>
    </>
  );
}

/* ── SERVICE DEMO COMPONENTS ── */

function VoiceDemo() {
  const msgs = [
    { ai: true,  text: "Thanks for calling! How can I help you today?" },
    { ai: false, text: "Hi, I need AC repair tomorrow if possible." },
    { ai: true,  text: "Of course! I have 10am or 2pm available. Which works?" },
    { ai: false, text: "2pm is perfect." },
    { ai: true,  text: "Booked! See you tomorrow at 2pm. Reminder on its way!" },
  ];
  return (
    <div style={{ width: "100%", borderRadius: 20, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.25), 0 0 0 1px rgba(199,177,103,.2)" }}>
      <div style={{ background: "#0d1117", padding: "18px 22px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ position: "relative" }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, #C7B167, #a8923e)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, boxShadow: "0 4px 20px rgba(199,177,103,.4)" }}>��</div>
          <div style={{ position: "absolute", bottom: 1, right: 1, width: 11, height: 11, borderRadius: "50%", background: "#4ade80", border: "2px solid #0d1117" }} />
        </div>
        <div>
          <div style={{ fontFamily: "var(--body)", fontSize: 14, fontWeight: 700, color: "#fff" }}>AI Receptionist</div>
          <div style={{ fontFamily: "var(--body)", fontSize: 11, color: "#4ade80", marginTop: 1 }}>● Appointment booked!</div>
        </div>
        <div style={{ marginLeft: "auto", padding: "3px 10px", borderRadius: 20, background: "rgba(74,222,128,.1)", border: "1px solid rgba(74,222,128,.2)" }}>
          <span style={{ fontFamily: "var(--body)", fontSize: 10, color: "#4ade80", fontWeight: 700 }}>24/7</span>
        </div>
      </div>
      <div style={{ background: "#111827", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.ai ? "flex-start" : "flex-end", gap: 8 }}>
            {m.ai && <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg, #C7B167, #a8923e)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, marginTop: 2 }}>🤖</div>}
            <div style={{ padding: "9px 14px", borderRadius: m.ai ? "3px 14px 14px 14px" : "14px 3px 14px 14px", background: m.ai ? "rgba(255,255,255,.07)" : "linear-gradient(135deg, #C7B167, #a8923e)", maxWidth: "78%", fontFamily: "var(--body)", fontSize: 12, color: m.ai ? "rgba(255,255,255,.85)" : "#161C2C", lineHeight: 1.55, fontWeight: m.ai ? 400 : 600 }}>{m.text}</div>
          </div>
        ))}
      </div>
      <div style={{ background: "#0d1117", padding: "14px 22px", display: "flex", justifyContent: "space-around", borderTop: "1px solid rgba(255,255,255,.04)" }}>
        {[{ val: "0s", label: "Wait time" }, { val: "24/7", label: "Always on" }, { val: "100%", label: "Calls answered" }].map((s, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: 18, color: "#C7B167" }}>{s.val}</div>
            <div style={{ fontFamily: "var(--body)", fontSize: 10, color: "rgba(255,255,255,.25)", marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TextBackDemo() {
  return (
    <div style={{ padding: "28px", borderRadius: 20, background: cream, border: "2px solid " + gold, width: "100%" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24, textAlign: "center" }}>
        {[{ val: "30s", label: "Response" }, { val: "24/7", label: "Availability" }, { val: "$0", label: "Per Call" }].map((s, i) => (
          <div key={i}>
            <div style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: 22, color: gold }}>{s.val}</div>
            <div style={{ fontFamily: "var(--body)", fontSize: 11, color: "#888" }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ background: navy, borderRadius: 12, padding: "16px", marginBottom: 16, textAlign: "center" }}>
        <div style={{ fontFamily: "var(--body)", fontSize: 11, color: "rgba(255,255,255,.4)", marginBottom: 6 }}>AI responded in</div>
        <div style={{ fontFamily: "var(--head)", fontWeight: 900, fontSize: 48, color: "#4ade80" }}>28s</div>
      </div>
      <div style={{ background: "#fff", borderRadius: 10, padding: "12px 14px", border: "1px solid #e8e4d8" }}>
        <div style={{ fontFamily: "var(--body)", fontSize: 11, color: gold, fontWeight: 600, marginBottom: 4 }}>AI Text Response</div>
        <div style={{ fontFamily: "var(--body)", fontSize: 13, color: "#444", lineHeight: 1.5 }}>Hi! We just missed your call. How can we help you today?</div>
      </div>
    </div>
  );
}

function ChatbotDemo() {
  const msgs = [
    { ai: true,  text: "Hi! How can I help you today? 👋" },
    { ai: false, text: "I need AC repair ASAP" },
    { ai: true,  text: "We can get someone out today! What time works best?" },
    { ai: false, text: "2pm works!" },
    { ai: true,  text: "Perfect! Booked for 2pm. Confirmation text on its way 📅" },
  ];
  return (
    <div style={{ width: "100%", borderRadius: 20, overflow: "hidden", border: "2px solid " + gold, boxShadow: "0 12px 40px rgba(22,28,44,.1)" }}>
      <div style={{ background: navy, padding: "14px 18px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#4ade80" }} />
        <span style={{ fontFamily: "var(--body)", fontSize: 13, fontWeight: 700, color: "#fff" }}>AI Assistant</span>
        <span style={{ fontFamily: "var(--body)", fontSize: 11, color: "rgba(255,255,255,.4)", marginLeft: "auto" }}>Always Online</span>
      </div>
      <div style={{ background: cream, padding: "16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.ai ? "flex-start" : "flex-end" }}>
            {m.ai && <div style={{ width: 28, height: 28, borderRadius: "50%", background: navy, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 8, flexShrink: 0, fontSize: 13 }}>🤖</div>}
            <div style={{ padding: "10px 14px", borderRadius: m.ai ? "4px 14px 14px 14px" : "14px 4px 14px 14px", background: m.ai ? "#fff" : gold, border: m.ai ? "1px solid #e8e4d8" : "none", maxWidth: "78%", fontFamily: "var(--body)", fontSize: 13, color: m.ai ? "#444" : navy, lineHeight: 1.5, fontWeight: m.ai ? 400 : 500 }}>{m.text}</div>
          </div>
        ))}
      </div>
      <div style={{ background: "#fff", padding: "10px 16px", borderTop: "1px solid #e8e4d8", display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, padding: "8px 12px", borderRadius: 8, background: "#f5f5f5", fontFamily: "var(--body)", fontSize: 12, color: "#aaa" }}>Type a message...</div>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: navy, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: gold, fontSize: 14 }}>→</span></div>
      </div>
    </div>
  );
}

function ReviewDemo() {
  return (
    <div style={{ padding: "32px", borderRadius: 20, background: cream, border: "2px solid " + gold, width: "100%", textAlign: "center" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "var(--body)", fontSize: 12, color: "#888", marginBottom: 8 }}>Google Business Profile</div>
        <div style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: 48, color: navy }}>53</div>
        <div style={{ fontFamily: "var(--body)", fontSize: 12, color: "#888" }}>Google Reviews</div>
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 20 }}>
        {[1,2,3,4,5].map(i => <span key={i} style={{ fontSize: 28 }}>⭐</span>)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {["Job Complete", "Review text sent automatically", "Customer left 5 stars ⭐", "Google ranking went up 📈"].map((l, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(74,222,128,.08)", border: "1px solid rgba(74,222,128,.2)" }}>
            <span style={{ fontSize: 14 }}>✅</span>
            <span style={{ fontFamily: "var(--body)", fontSize: 12, color: "#166534", fontWeight: 600 }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MaintenanceDemo() {
  return (
    <div style={{ padding: "32px", borderRadius: 20, background: navy, border: "2px solid " + gold, width: "100%" }}>
      <div style={{ fontFamily: "var(--body)", fontSize: 12, color: "rgba(255,255,255,.4)", marginBottom: 16, textAlign: "center" }}>Monthly Site Health Check</div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontFamily: "var(--body)", fontSize: 12, color: "rgba(255,255,255,.6)" }}>Progress</span>
          <span style={{ fontFamily: "var(--body)", fontSize: 12, color: gold, fontWeight: 600 }}>100%</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,.1)" }}>
          <div style={{ height: "100%", width: "100%", borderRadius: 4, background: "linear-gradient(90deg, " + gold + ", #4ade80)" }} />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {["Security patches applied", "Page speed optimized", "Content updated", "Uptime confirmed", "All systems green ✓"].map((t, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(74,222,128,.08)", border: "1px solid rgba(74,222,128,.2)" }}>
            <span style={{ fontSize: 12 }}>✅</span>
            <span style={{ fontFamily: "var(--body)", fontSize: 12, color: "#4ade80", fontWeight: 500 }}>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinanceDemo() {
  const months = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const heights = [65, 72, 58, 80, 75, 100];
  return (
    <div style={{ padding: "28px", borderRadius: 20, background: cream, border: "2px solid " + gold, width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: "var(--body)", fontSize: 11, color: "#888" }}>Monthly Revenue</div>
          <div style={{ fontFamily: "var(--head)", fontWeight: 800, fontSize: 28, color: navy }}>$18,700</div>
        </div>
        <div style={{ padding: "4px 10px", borderRadius: 20, background: "rgba(74,222,128,.1)", border: "1px solid rgba(74,222,128,.2)" }}>
          <span style={{ fontFamily: "var(--body)", fontSize: 11, color: "#166534", fontWeight: 600 }}>↑ 17% vs last month</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 100, marginBottom: 8 }}>
        {heights.map((h, i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ width: "100%", borderRadius: "4px 4px 0 0", background: i === 5 ? gold : "rgba(199,177,103,.35)", height: h + "%" }} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {months.map((m, i) => <div key={i} style={{ flex: 1, fontFamily: "var(--body)", fontSize: 10, color: i === 5 ? navy : "#aaa", textAlign: "center", fontWeight: i === 5 ? 700 : 400 }}>{m}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[{ label: "Pay yourself", val: "$3,200" }, { label: "Set aside taxes", val: "$1,870" }].map((s, i) => (
          <div key={i} style={{ padding: "10px 12px", borderRadius: 8, background: "#fff", border: "1px solid #e8e4d8", textAlign: "center" }}>
            <div style={{ fontFamily: "var(--body)", fontSize: 10, color: "#888", marginBottom: 2 }}>{s.label}</div>
            <div style={{ fontFamily: "var(--head)", fontWeight: 700, fontSize: 16, color: gold }}>{s.val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WebsiteDemo() {
  return (
    <div style={{ padding: "24px", borderRadius: 20, background: cream, border: "2px solid " + gold, width: "100%" }}>
      <div style={{ background: navy, borderRadius: 10, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
        {["#ff5f57","#ffbd2e","#28c840"].map((c, i) => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />)}
        <div style={{ flex: 1, background: "rgba(255,255,255,.08)", borderRadius: 4, padding: "3px 8px", marginLeft: 6 }}>
          <span style={{ fontFamily: "var(--body)", fontSize: 10, color: "rgba(255,255,255,.4)" }}>yoursite.com</span>
        </div>
      </div>
      <div style={{ background: "#fff", borderRadius: 8, overflow: "hidden", border: "1px solid #e8e4d8" }}>
        <div style={{ background: navy, height: 40, display: "flex", alignItems: "center", padding: "0 12px", gap: 8 }}>
          <div style={{ fontFamily: "var(--body)", fontSize: 10, color: "#fff", fontWeight: 700 }}>Your Business</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {["Home","Services","Contact"].map(l => <span key={l} style={{ fontFamily: "var(--body)", fontSize: 9, color: "rgba(255,255,255,.6)" }}>{l}</span>)}
          </div>
        </div>
        <div style={{ padding: "16px 12px" }}>
          <div style={{ height: 8, borderRadius: 4, background: navy, width: "70%", marginBottom: 8 }} />
          <div style={{ height: 5, borderRadius: 3, background: "#ddd", width: "90%", marginBottom: 4 }} />
          <div style={{ height: 5, borderRadius: 3, background: "#ddd", width: "75%", marginBottom: 16 }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
            {[0,1,2].map(i => <div key={i} style={{ height: 50, borderRadius: 6, background: i === 1 ? gold : cream, border: "1px solid " + gold }} />)}
          </div>
          <div style={{ height: 28, borderRadius: 6, background: navy, width: "50%", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: "var(--body)", fontSize: 9, color: "#fff", fontWeight: 700 }}>Book Now</span>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 6, justifyContent: "center" }}>
        {["Design", "Mobile", "SEO", "Live"].map((l) => (
          <div key={l} style={{ padding: "3px 8px", borderRadius: 10, background: "rgba(74,222,128,.1)", border: "1px solid rgba(74,222,128,.3)" }}>
            <span style={{ fontFamily: "var(--body)", fontSize: 9, color: "#166834", fontWeight: 600 }}>✓ {l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
